import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  message,
  Space,
  Typography,
  Card,
  Avatar,
  Row,
  Col,
  Divider,
  Modal
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  EnvironmentOutlined,
  HomeOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;
const { TextArea } = Input;

const Clients = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL; // ✅ Declared globally within the component

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingClient, setEditingClient] = useState(null);
  const [searchText, setSearchText] = useState('');

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/clients`);
      const data = Array.isArray(res.data) ? res.data : [];
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      message.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter(client =>
      client.Name.toLowerCase().includes(searchText.toLowerCase()) ||
      client.Email.toLowerCase().includes(searchText.toLowerCase()) ||
      (client.Number && client.Number.toLowerCase().includes(searchText.toLowerCase()))
    );
    setFilteredClients(filtered);
  }, [searchText, clients]);

  const showDrawer = (client = null) => {
    setEditingClient(client);
    form.resetFields();
    if (client) {
      form.setFieldsValue(client);
    }
    setIsDrawerVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingClient) {
        await axios.put(`${baseUrl}/clients/${editingClient.ClientID}`, values);
        message.success('Client updated successfully');
      } else {
        await axios.post(`${baseUrl}/clients`, values);
        message.success('Client added successfully');
      }
      setIsDrawerVisible(false);
      fetchClients();
      setSearchText('');
    } catch (error) {
      console.error('Error saving client:', error);
      message.error(`Failed to ${editingClient ? 'update' : 'add'} client`);
    }
  };

  const confirmDeleteClient = (client) => {
    Modal.confirm({
      title: 'Delete Client',
      content: `Are you sure you want to delete client "${client.Name}"?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      maskStyle: { backdropFilter: 'blur(8px)' },
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/clients/${client.ClientID}`);
          message.success('Client deleted successfully');
          fetchClients();
        } catch (error) {
          console.error('Error deleting client:', error);
          message.error('Failed to delete client');
        }
      },
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const columns = [
    {
      title: 'Sr No',
      key: 'srNo',
      width: 80,
      render: (_, record, index) => (
        <Text strong style={{ 
          color: '#1890ff',
          fontSize: '14px'
        }}>
          {index + 1}
        </Text>
      ),
      align: 'center'
    },
    {
      title: 'Client',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar size="large" style={{ marginRight: 12, backgroundColor: '#1890ff', color: '#fff' }}>
            {text ? text.charAt(0).toUpperCase() : '?'}
          </Avatar>
          <div>
            <Text strong style={{ display: 'block' }}>{text}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.Email || 'No email'}
            </Text>
          </div>
        </div>
      ),
      width: 250
    },
    {
      title: 'City',
      dataIndex: 'Email',
      key: 'City',
      sorter: (a, b) => a.Email.localeCompare(b.Email),
      render: (text) => (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {text || 'No email'}
          </Text>
        </div>
      ),
      width: 250
    },
    {
      title: 'Contact',
      key: 'contact',
      sorter: (a, b) => (a.Number || '').localeCompare(b.Number || ''),
      render: (_, record) => (
        <div>
          {record.Number && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <Text>{record.Number}</Text>
            </div>
          )}
          {record.Whatsapp && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <WhatsAppOutlined style={{ marginRight: 8, color: '#25D366' }} />
              <Text>{record.Whatsapp}</Text>
            </div>
          )}
        </div>
      ),
      width: 200
    },
    {
      title: 'Address',
      dataIndex: 'Address',
      key: 'Address',
      sorter: (a, b) => (a.Address || '').localeCompare(b.Address || ''),
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <EnvironmentOutlined style={{ marginRight: 8, color: '#FF4D4F', marginTop: 2 }} />
          <Text>{text || 'No address provided'}</Text>
        </div>
      ),
      ellipsis: true
    },
    {
      title: 'Menu',
      key: 'actions',
      align: 'left',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => showDrawer(record)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => confirmDeleteClient(record)} />
        </Space>
      ),
      width: 80
    },
  ];

  return (
    <div>
      <Card
        bordered={false}
        style={{ boxShadow: 'none', borderRadius: 8 }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Clients</Title>
            <Text type="secondary">Manage your client database</Text>
          </Col>
          <Col>
            <Space>
              <Input.Search
                placeholder="Search clients..."
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                onSearch={handleSearch}
                onChange={e => handleSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => showDrawer()} style={{ borderRadius: 6 }}>
                New Client
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={filteredClients}
          columns={columns}
          rowKey="ClientID"
          loading={loading}
          pagination={false}
          scroll={{ x: '100%' }}
          bordered
          size="small"
        />
      </Card>

      {/* Client Drawer */}
      <Drawer
        title={editingClient ? `Edit ${editingClient.Name}` : 'Add New Client'}
        open={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        width={600}
        extra={
          <Space>
            <Button onClick={() => setIsDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}>
              {editingClient ? 'Save Changes' : 'Create Client'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="Name" label="Full Name" rules={[{ required: true, message: 'Please enter client name' }]}>
                <Input prefix={<UserOutlined />} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Email"
                label="City"
                rules={[
                  { required: true, message: 'Please enter city' },
                ]}
              >
                <Input prefix={<HomeOutlined />} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="Number" label="Phone Number">
                <Input prefix={<PhoneOutlined />} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="Whatsapp" label="WhatsApp Number">
                <Input prefix={<WhatsAppOutlined />} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="Address" label="Address">
            <TextArea rows={3} size="large" />
          </Form.Item>

          {editingClient && (
            <>
              <Divider />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Created: {new Date().toLocaleDateString()} | Last updated: {new Date().toLocaleDateString()}
              </Text>
            </>
          )}
        </Form>
      </Drawer>
    </div>
  );
};

export default Clients;
